import type { ApiError, ApiResult } from '@/shared/api/types';

/**
 * Caché de consultas en memoria (el núcleo de `useApiQuery`, sin React): una entrada por clave con
 * deduplicación de peticiones en vuelo, antigüedad (`staleMs`), datos "semilla" (snapshot persistido que se
 * pinta al instante) y suscripción para que los hooks se actualicen. Mantiene los datos viejos si una
 * revalidación falla (stale-while-error).
 */
export interface QuerySnapshot<T> {
  data: T | undefined;
  error: ApiError | null;
  /** Date.now() de la última respuesta buena; null si nunca llegó (p. ej. solo hay una semilla). */
  updatedAt: number | null;
  isFetching: boolean;
  /** Los datos son una semilla (snapshot persistido) todavía sin confirmar con la red. */
  isSeed: boolean;
}

type Fetcher<T> = (signal: AbortSignal) => Promise<ApiResult<T>>;
type Listener = () => void;

interface Slot {
  snap: QuerySnapshot<unknown>;
  listeners: Set<Listener>;
  inflight: Promise<ApiResult<unknown>> | null;
  controller: AbortController | null;
  /** Hooks montados con esta clave (para cancelar cuando nadie la usa). */
  holders: number;
  /** Funciones que fuerzan una revalidación (las registran los hooks montados). */
  refetchers: Set<() => void>;
}

const VACIA: QuerySnapshot<never> = {
  data: undefined,
  error: null,
  updatedAt: null,
  isFetching: false,
  isSeed: false,
};

const cache = new Map<string, Slot>();

function slotDe(key: string): Slot {
  let slot = cache.get(key);
  if (!slot) {
    slot = {
      snap: VACIA,
      listeners: new Set(),
      inflight: null,
      controller: null,
      holders: 0,
      refetchers: new Set(),
    };
    cache.set(key, slot);
  }
  return slot;
}

function publicar(slot: Slot, cambios: Partial<QuerySnapshot<unknown>>): void {
  slot.snap = { ...slot.snap, ...cambios };
  slot.listeners.forEach((l) => l());
}

export function getSnapshot<T>(key: string): QuerySnapshot<T> {
  return (cache.get(key)?.snap ?? VACIA) as QuerySnapshot<T>;
}

export function subscribe(key: string, listener: Listener): () => void {
  const slot = slotDe(key);
  slot.listeners.add(listener);
  return () => slot.listeners.delete(listener);
}

/** ¿Hay datos y llegaron hace menos de `staleMs`? */
export function isFresh(key: string, staleMs: number): boolean {
  const { updatedAt } = getSnapshot(key);
  return updatedAt !== null && Date.now() - updatedAt < staleMs;
}

/** Pone datos de arranque (snapshot persistido) solo si la clave está vacía. No cuenta como "fresco". */
export function seedQueryData<T>(key: string, data: T): void {
  const slot = slotDe(key);
  if (slot.snap.data === undefined) publicar(slot, { data, isSeed: true });
}

/** Reemplaza los datos (p. ej. tras una mutación): quedan frescos. */
export function setQueryData<T>(key: string, data: T | ((previo: T | undefined) => T)): void {
  const slot = slotDe(key);
  const siguiente =
    typeof data === 'function'
      ? (data as (p: T | undefined) => T)(slot.snap.data as T | undefined)
      : data;
  publicar(slot, { data: siguiente, error: null, updatedAt: Date.now(), isSeed: false });
}

export interface FetchOptions {
  /** Tiempo (ms) durante el que los datos se consideran frescos y no se vuelve a pedir. */
  staleMs?: number;
  /** Pide aunque estén frescos. */
  force?: boolean;
}

/**
 * Obtiene la consulta: devuelve los datos frescos de la caché o hace UNA petición (compartida con quien la
 * pida a la vez). Nunca lanza. Si la cancelaron (nadie la usa ya) se ignora el resultado.
 */
export function fetchQuery<T>(
  key: string,
  fetcher: Fetcher<T>,
  { staleMs = 60_000, force = false }: FetchOptions = {},
): Promise<ApiResult<T>> {
  const slot = slotDe(key);
  if (slot.inflight) return slot.inflight as Promise<ApiResult<T>>;
  if (!force && slot.snap.data !== undefined && isFresh(key, staleMs)) {
    return Promise.resolve({ ok: true, data: slot.snap.data as T });
  }
  const controller = new AbortController();
  slot.controller = controller;
  publicar(slot, { isFetching: true });
  const promesa = fetcher(controller.signal).then((r) => {
    if (slot.controller === controller) {
      slot.inflight = null;
      slot.controller = null;
      if (!controller.signal.aborted) {
        if (r.ok)
          publicar(slot, {
            data: r.data,
            error: null,
            updatedAt: Date.now(),
            isFetching: false,
            isSeed: false,
          });
        else publicar(slot, { error: r, isFetching: false });
      } else publicar(slot, { isFetching: false });
    }
    return r;
  });
  slot.inflight = promesa;
  return promesa;
}

/** Los hooks avisan que usan la clave; al soltar la última se cancela la petición en vuelo. */
export function retener(key: string, refetch: () => void): () => void {
  const slot = slotDe(key);
  slot.holders += 1;
  slot.refetchers.add(refetch);
  return () => {
    slot.holders -= 1;
    slot.refetchers.delete(refetch);
    if (slot.holders <= 0 && slot.controller) {
      slot.controller.abort();
      slot.controller = null;
      slot.inflight = null;
      publicar(slot, { isFetching: false });
    }
  };
}

const coincide = (key: string, prefijo: string): boolean => key.startsWith(prefijo);

/**
 * Marca como viejas las consultas cuya clave empieza por `prefijo` (todas si se omite) y revalida las que
 * están en pantalla. Las demás se piden la próxima vez que se usen.
 */
export function invalidateQueries(prefijo = ''): void {
  for (const [key, slot] of cache) {
    if (!coincide(key, prefijo)) continue;
    if (slot.snap.updatedAt !== null) slot.snap = { ...slot.snap, updatedAt: 0 };
    slot.refetchers.forEach((r) => r());
  }
}

export function clearQueryCache(): void {
  for (const slot of cache.values()) slot.controller?.abort();
  cache.clear();
}

/** Prefijos de claves con datos de la cuenta: se borran al cerrar sesión. */
export const PREFIJOS_PRIVADOS = ['alertas', 'notificaciones', 'sesiones', 'yo'] as const;

export function clearPrivateQueries(): void {
  for (const key of [...cache.keys()]) {
    if (!PREFIJOS_PRIVADOS.some((p) => key.startsWith(p))) continue;
    cache.get(key)?.controller?.abort();
    const slot = cache.get(key);
    if (slot) publicar(slot, { ...VACIA, isFetching: false });
  }
}
